import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { fmtTL } from "@/lib/format";
import { Badge, StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Eye, Edit, FileText, ShoppingCart, Wallet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";

export default function Companies() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [areas, setAreas] = useState([]);
  const [q, setQ] = useState("");
  const [area, setArea] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (area !== "all") params.service_area_id = area;
      const { data } = await api.get("/companies", { params });
      setCompanies(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/definitions/service_areas").then((r) => setAreas(r.data));
    load();
  }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, area]);

  const areaMap = Object.fromEntries(areas.map((a) => [a.id, a.name]));
  const canEdit = hasRole(user, "admin", "sales");
  const canDelete = user?.role === "admin";

  const remove = async (id) => {
    if (!window.confirm("Bu firmayı kalıcı olarak silmek istediğinize emin misiniz? İlgili teklif, ödeme ve cari kayıtları da silinecektir.")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Firma silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-4">
      <div className="azd-card p-4 lg:p-5">
        <div className="grid lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Ara</label>
            <Input data-testid="companies-search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Firma adı, ünvan, ilgili kişi..." />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Hizmet Alanı</label>
            <Select value={area} onValueChange={setArea}>
              <SelectTrigger data-testid="companies-area-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {areas.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-[#6B7280] font-medium">
          Toplam <span className="text-[#08285A] font-bold">{companies.length}</span> firma
        </div>
        <div className="flex gap-2">
          <ExportButton type="companies" />
          {canEdit && (
            <Button onClick={() => navigate("/companies/new")} data-testid="new-company-btn"
                    className="bg-[#0B3676] hover:bg-[#08285A] text-white">
              <Plus size={16} className="mr-1" /> Yeni Firma
            </Button>
          )}
        </div>
      </div>

      <div className="azd-card overflow-hidden">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Firma</th>
                <th className="text-left px-4 py-3 font-semibold">Hizmet Alanı</th>
                <th className="text-left px-4 py-3 font-semibold">İlgili Kişi</th>
                <th className="text-left px-4 py-3 font-semibold">Telefon</th>
                <th className="text-right px-4 py-3 font-semibold">Vade</th>
                <th className="text-right px-4 py-3 font-semibold">Toplam Borç</th>
                <th className="text-right px-4 py-3 font-semibold">Gecikmiş</th>
                <th className="text-right px-4 py-3 font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-[#6B7280]">Yükleniyor...</td></tr>
              ) : companies.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-[#6B7280]">Firma bulunamadı</td></tr>
              ) : companies.map((c) => (
                <tr key={c.id} data-testid={`company-row-${c.id}`}
                    className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#111827]">{c.company_name}</div>
                    <div className="text-xs text-[#6B7280]">{c.company_title}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <Badge variant="info">{areaMap[c.service_area_id] || "-"}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm">{c.contact_person}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280] font-mono">{c.mobile_phone}</td>
                  <td className="px-4 py-3 text-right text-sm">{c.payment_term_days} gün</td>
                  <td className="px-4 py-3 text-right font-bold text-[#0B3676]">
                    {fmtTL(c.summary?.remaining_debt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.summary?.overdue_debt > 0 ? (
                      <span className="text-[#DC2626] font-bold">{fmtTL(c.summary.overdue_debt)}</span>
                    ) : (
                      <span className="text-[#6B7280]">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => navigate(`/companies/${c.id}`)}
                            data-testid={`view-company-${c.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#0B3676] hover:bg-[#0B3676]/10 rounded"
                            title="Görüntüle"><Eye size={14} /></button>
                    {canEdit && (
                      <>
                        <button onClick={() => navigate(`/companies/${c.id}/edit`)}
                                className="p-1.5 text-[#6B7280] hover:text-[#0B3676] hover:bg-[#0B3676]/10 rounded ml-1"
                                title="Düzenle"><Edit size={14} /></button>
                        <button onClick={() => navigate(`/quotes/new?company=${c.id}`)}
                                className="p-1.5 text-[#6B7280] hover:text-[#0B3676] hover:bg-[#0B3676]/10 rounded ml-1"
                                title="Teklif"><FileText size={14} /></button>
                        <button onClick={() => navigate(`/orders/new?company=${c.id}`)}
                                className="p-1.5 text-[#6B7280] hover:text-[#0B3676] hover:bg-[#0B3676]/10 rounded ml-1"
                                title="Sipariş"><ShoppingCart size={14} /></button>
                        {canDelete && (
                          <button onClick={() => remove(c.id)}
                                  data-testid={`delete-company-${c.id}`}
                                  className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                                  title="Sil"><Trash2 size={14} /></button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
